/**
 * ToolResultStore — browser-side persistence for a tool's LAST successful
 * result, so a finished export survives a tab close / refresh / crash.
 *
 * Why this exists (2026-08-06, user 4126 / support conv #553): a Premium
 * user ran /video-flip on a 778 MB, 2 h 03 m movie. Browser-side encodes of
 * that length take minutes (hardware path) to hours (FFmpeg WASM); the
 * result lived only in a blob: URL inside the tab, so closing/refreshing
 * the page destroyed the work — his words: «при обновлении страницы весь
 * успех монтажа аннулируется», then «после 100% трансформации сервис не
 * даёт возможности скачать результат». He went looking for the file in
 * /my-generations, where browser-side results have never been.
 *
 * Storage: OPFS (navigator.storage.getDirectory) — origin-scoped, quota is
 * effectively disk-based on desktop, survives restarts, and (unlike blob
 * URLs) is not tied to the document lifetime. Layout:
 *
 *     tool-results/<slug>/result.bin    — the output bytes
 *     tool-results/<slug>/meta.json     — { name, mime, size, savedAt }
 *
 * One result per tool; a new save replaces the previous one. Results older
 * than TTL_MS (48 h) are deleted on the next latest() call — this is a
 * courtesy hand-back, not a library (that's /my-generations' job for
 * server-side artifacts).
 *
 * Every method is best-effort and never throws: persistence failing must
 * not break the export flow it decorates. Callers check `supported()` for
 * UI decisions but may call save()/latest() unconditionally.
 *
 * ⚠️ Blob ≥ 2 GiB is unreadable in Chromium (see
 * memory reference_download_primitive) — write goes through createWritable
 * with the WHOLE blob (streams internally, no arrayBuffer round-trip), so
 * large results are fine; only genuinely-broken blobs fail, and that
 * failure is swallowed.
 *
 * Safari 15.2–16.x has OPFS but NO createWritable (only the worker-bound
 * createSyncAccessHandle) — the shared `__OpfsWriter` primitive
 * (public/js/opfs-writer.js) owns that fallback, proven on /join's
 * tree-merge (118 Safari users hit exactly this hole before it existed).
 * When the page loads opfs-writer.js, save() routes through it; without
 * it, the plain createWritable path serves every non-ancient browser.
 */
(function () {
'use strict';

var TTL_MS = 48 * 60 * 60 * 1000;
var ROOT_DIR = 'tool-results';

function supported() {
    if (!(navigator.storage && navigator.storage.getDirectory)) return false;
    if (typeof FileSystemFileHandle === 'undefined') return false;
    if (FileSystemFileHandle.prototype.createWritable) return true;
    // old-Safari sync-handle mode — usable only through the shared writer
    return !!(window.__OpfsWriter && window.__OpfsWriter.detectMode &&
              window.__OpfsWriter.detectMode() === 'syncAccessHandle');
}

async function toolDir(slug, create) {
    var root = await navigator.storage.getDirectory();
    var base = await root.getDirectoryHandle(ROOT_DIR, { create: !!create });
    return base.getDirectoryHandle(String(slug).replace(/[^\w-]/g, '_'), { create: !!create });
}

// Chunked fallback writer for pages that don't load opfs-writer.js. A single
// whole-blob `w.write(blob)` was MEASURED at ~4 MB/s cold (550 MB → 135 s,
// probe 2026-08-06) — one giant IPC copy; the same bytes in 8 MB slices
// stream at disk speed (10-18 s). A minutes-long save is also a minutes-wide
// window for a tab close to abort the transactional writable — exactly how
// the first big-result save was lost.
async function chunkedWrite(dir, name, blob) {
    var fh = await dir.getFileHandle(name, { create: true });
    var w = await fh.createWritable();
    var STEP = 8 * 1024 * 1024;
    for (var off = 0; off < blob.size; off += STEP) {
        await w.write(blob.slice(off, Math.min(blob.size, off + STEP)));
    }
    await w.close();
}

async function save(slug, blob, meta) {
    if (!supported() || !blob || !blob.size) return false;
    var t0 = Date.now();
    try {
        // Best-effort durability: without persist(), best-effort OPFS is
        // EVICTABLE under storage pressure — Yandex Browser does it
        // aggressively (screen-recorder lost 4 multi-minute recordings/7d
        // to exactly this before adding the call; см. память
        // reference_long_recording_patterns). Chromium auto-decides from
        // site engagement — no prompt; a refusal just leaves us best-effort.
        // ⚠️ persist() answers with a PROMISE, and a try/catch around the call sees
        // nothing of its rejection. On an iPhone whose page has no storage access
        // (Safari private mode, «Block all cookies») WebKit rejects it with
        // `TypeError: Context not access storage`, and that rejection surfaced as
        // an UNHANDLED one from the export flow this store only decorates (Sentry
        // TIMBRICA-FRONTEND-KG, /audio-enhancer, iOS 18.7). The answer is advisory
        // either way — take it where it is produced.
        try {
            var persisting = navigator.storage.persist && navigator.storage.persist();
            if (persisting && typeof persisting.catch === 'function') persisting.catch(function () {});
        } catch (_) {}
        var dir = await toolDir(slug, true);
        var metaBlob = new Blob([JSON.stringify({
            name: (meta && meta.name) || 'result',
            mime: blob.type || (meta && meta.mime) || 'application/octet-stream',
            size: blob.size,
            savedAt: Date.now()
        })], { type: 'application/json' });
        if (window.__OpfsWriter && window.__OpfsWriter.writeBlob) {
            // shared writer picks createWritable or the Safari sync-handle worker
            await window.__OpfsWriter.writeBlob(dir, 'result.bin', blob);
            await window.__OpfsWriter.writeBlob(dir, 'meta.json', metaBlob);
        } else {
            await chunkedWrite(dir, 'result.bin', blob);
            await chunkedWrite(dir, 'meta.json', metaBlob);
        }
        try { window.__ae && window.__ae('feature_use', { slug: slug, action: 'result_store_saved', sizeMB: Math.round(blob.size / 1048576), ms: Date.now() - t0 }); } catch (_) {}
        return true;
    } catch (e) {
        // QuotaExceededError on tight disks, SecurityError in some embeds —
        // all non-fatal: the in-page result panel still works.
        try { window.__ae && window.__ae('feature_use', { slug: slug, action: 'result_store_save_failed', err: String((e && e.name) || e).slice(0, 60), sizeMB: Math.round(blob.size / 1048576), ms: Date.now() - t0 }); } catch (_) {}
        return false;
    }
}

// → { blob, file, meta } | null.  Deletes and reports null when expired.
async function latest(slug) {
    if (!supported()) return null;
    try {
        var dir = await toolDir(slug, false);
        var mh = await dir.getFileHandle('meta.json');
        var meta = JSON.parse(await (await mh.getFile()).text());
        if (!meta || !meta.savedAt || (Date.now() - meta.savedAt) > TTL_MS) {
            await clear(slug);
            return null;
        }
        var fh = await dir.getFileHandle('result.bin');
        var file = await fh.getFile();
        if (!file.size || file.size !== meta.size) { await clear(slug); return null; }
        return { blob: file, file: file, meta: meta };
    } catch (_) {
        return null;   // no stored result — the overwhelmingly common case
    }
}

async function clear(slug) {
    if (!supported()) return;
    try {
        var root = await navigator.storage.getDirectory();
        var base = await root.getDirectoryHandle(ROOT_DIR);
        await base.removeEntry(String(slug).replace(/[^\w-]/g, '_'), { recursive: true });
    } catch (_) {}
}

window.ToolResultStore = {
    supported: supported,
    save: save,
    latest: latest,
    clear: clear,
    version: '1.1.0'
};

})();
